/**
 * Temu 色块图：从主图中心裁 80×80 并上传（不生成方块图；对齐 Shein「用户无需手传色块」）。
 */

import { temuPlatformAPI } from '@/utils/api';
import { adminAPI } from '@/utils/adminApi';
import { cropFileToTemuColorBlock } from '@/temu-platform/utils/temuProductImage';
import { toCanonicalUploadsPath } from '@/temu-platform/modules/products/components/publishImageUploadThumbSrc';
import { isSignedViewPath } from '@/utils/imageDisplayAccess';
import { isLikelyBrowserReachableThumb, pickBrowserPreviewImageUrl } from '@/temu-platform/modules/products/components/TemuProductThumb';
import { fileFromTemuImageSlot } from '@/temu-platform/utils/temuMaterialFromMain';

function imageUrlOf(image) {
  if (!image) return '';
  return String(
    image.temuUrl
    || image.publicUrl
    || image.ipeUrl
    || image.previewUrl
    || image.url
    || '',
  ).trim();
}

function buildUploadedSlot(file, res, { requireTemuUrl = true } = {}) {
  const temuUrl = String(res?.temuUrl || res?.url || '').trim();
  if (requireTemuUrl && !temuUrl) {
    throw new Error(res?.message || res?.msg || '色块图上传未返回 TEMU 地址，请重试');
  }
  const rawIpe = String(res?.ipeUrl || res?.originalUrl || res?.storedUrl || '').trim();
  const rawPublic = String(
    res?.publicUrl
    || res?.previewUrlDisplay
    || res?.originalUrlDisplay
    || res?.viewUrl
    || res?.displayUrl
    || '',
  ).trim();
  const durable = toCanonicalUploadsPath(rawIpe)
    || toCanonicalUploadsPath(rawPublic)
    || rawIpe
    || rawPublic
    || '';
  let previewUrl = '';
  try {
    if (typeof URL !== 'undefined' && file) {
      previewUrl = URL.createObjectURL(file);
    }
  } catch {
    /* ignore */
  }
  if (!previewUrl) {
    const fallback = pickBrowserPreviewImageUrl(rawPublic, durable, rawIpe, temuUrl);
    previewUrl = (fallback && isLikelyBrowserReachableThumb(fallback)) ? fallback : durable;
  }
  return {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    name: file?.name || 'color-block.jpg',
    previewUrl,
    publicUrl: isSignedViewPath(rawPublic) ? rawPublic : durable,
    ipeUrl: durable,
    temuUrl,
  };
}

/**
 * 从主图槽位派生色块图并上传。
 * @returns {Promise<object|null>} colorBlockImage slot
 */
export async function deriveTemuColorBlockFromMainSlot({
  shopId,
  mainSlot,
  skipTemuConvert = false,
} = {}) {
  if (!mainSlot) return null;
  const sourceFile = await fileFromTemuImageSlot(mainSlot);
  if (!sourceFile) {
    throw new Error('无法读取主图以生成色块图，请重新上传主图');
  }
  const cropped = await cropFileToTemuColorBlock(sourceFile);
  if (skipTemuConvert) {
    const res = await adminAPI.storeOfficialTemuProductTemplateImage(cropped);
    return buildUploadedSlot(cropped, res, { requireTemuUrl: false });
  }
  if (!shopId) {
    throw new Error('请先选择店铺后再生成色块图');
  }
  // 色块裁剪图仅上 Temu，不写入 OSS/图片空间
  const res = await temuPlatformAPI.uploadProductPic(shopId, cropped, {
    imageBizType: 0,
    fromImageSpace: true,
  });
  return buildUploadedSlot(cropped, res, { requireTemuUrl: true });
}

/**
 * 发布/存草稿前：对缺少色块图的 SKC 按主图自动补齐（用户无需手传）。
 */
export async function ensureTemuColorBlocksOnForm(form, {
  shopId,
  skipTemuConvert = false,
} = {}) {
  const skcList = Array.isArray(form?.skcList) ? form.skcList : [];
  if (!skcList.length) return form;
  let changed = false;
  const nextSkcList = [];
  for (const skc of skcList) {
    if (imageUrlOf(skc?.colorBlockImage) || !imageUrlOf(skc?.mainImage)) {
      nextSkcList.push(skc);
      continue;
    }
    try {
      const colorBlock = await deriveTemuColorBlockFromMainSlot({
        shopId,
        mainSlot: skc.mainImage,
        skipTemuConvert,
      });
      if (colorBlock) {
        changed = true;
        nextSkcList.push({ ...skc, colorBlockImage: colorBlock, squareImage: null });
      } else {
        nextSkcList.push(skc);
      }
    } catch {
      nextSkcList.push(skc);
    }
  }
  if (!changed) return form;
  return { ...form, skcList: nextSkcList };
}
